import { useState, type ReactNode } from 'react';
import { Card } from './primitives.js';
import { Icon } from './icons.js';

const KEY = (id: string): string => `erectus.section.${id}`;

function storedOpen(id: string, fallback: boolean): boolean {
  try {
    const value = window.localStorage.getItem(KEY(id));
    return value === null ? fallback : value === '1';
  } catch {
    return fallback;
  }
}

/**
 * A card whose body folds away under its title. Folded, it still says what
 * is set, in one line, so the screen stays short without hiding state. The
 * open/closed choice is remembered on this device.
 */
export function CollapsibleCard({
  id,
  title,
  summary,
  defaultOpen = false,
  className = '',
  children,
}: {
  id: string;
  title: string;
  summary?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(() => storedOpen(id, defaultOpen));
  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(KEY(id), next ? '1' : '0');
    } catch {
      // Remembering it is a convenience.
    }
  };

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`section-${id}`}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold tracking-wide text-muted uppercase">{title}</span>
          {!open && summary ? <span className="mt-1 block truncate text-sm text-ink-soft">{summary}</span> : null}
        </span>
        <Icon
          name="back"
          size={18}
          className={`shrink-0 text-muted transition-transform ${open ? 'rotate-90' : '-rotate-90'}`}
        />
      </button>
      {open ? (
        <div id={`section-${id}`} className={`px-4 pb-4 ${className}`}>
          {children}
        </div>
      ) : null}
    </Card>
  );
}
